export interface NavLink {
    label: string
    href: string
}

export interface Stat {
    value: number
    suffix: string
    label: string
}

export interface Principle {
    title: string
    text: string
    icon: 'sparkle' | 'check' | 'heart'
}

export interface Service {
    id: string
    title: string
    summary: string
    deliverables: ReadonlyArray<string>
}

export type AudienceIcon = 'robot' | 'eye' | 'cloud' | 'heart'

export interface Audience {
    id: string
    title: string
    text: string
    icon: AudienceIcon
}

export type LevelIcon = 'egg' | 'hatch' | 'footprint' | 'crown'

export interface Level {
    level: string
    stage: string
    title: string
    text: string
    icon: LevelIcon
}

export interface TeamMember {
    name: string
    role: string
    image: string
    move: string
    stats: ReadonlyArray<{ label: string, value: number }>
}

export const NAV_LINKS: ReadonlyArray<NavLink> = [
    { label: "who we are", href: "#who-we-are" },
    { label: "our services", href: "#services" },
    { label: "services for", href: "#services-for" },
    { label: "team", href: "#team" },
    { label: "contacts", href: "#contacts" }
]

export const HERO = {
    eyebrow: "digital marketing studio",
    lines: ["MARKETING", "IS OUR ART"],
    subtitle: "success is your reality",
    cta: "let's start!",
    secondary: "see what we do",
    leftNote: "digital marketing & creative solutions.",
    rightNote: "it's time to open the door to the incredible world of marketing, where impulsive ideas meet unpredictable possibilities."
}

export const INTRO_TEXT = "Would you like to increase your brand awareness, attract new customers or improve conversion? Thanks to our creative approach, your website will turn into a real dinosaur in the online space! It will attract eyes, capture hearts and be remembered by every visitor. We understand that first impressions are everything, and we make sure your website not only meets your needs, but also leaves a lasting impression on your target audience."

export const STATS: ReadonlyArray<Stat> = [
    { value: 20, suffix: "+", label: "years of experience" },
    { value: 8500, suffix: "+", label: "completed projects" },
    { value: 12, suffix: "+", label: "countries for cooperation" }
]

export const MARQUEE_TOP: ReadonlyArray<string> = [
    "branding",
    "social media",
    "seo",
    "web design",
    "paid ads",
    "content",
    "video"
]

export const MARQUEE_BOTTOM: ReadonlyArray<string> = [
    "bold ideas",
    "real results",
    "pixel perfect",
    "data driven",
    "zero boring",
    "big roar"
]

export const WHO_WE_ARE = {
    heading: "WHO WE ARE?",
    fileName: "about_dinolab.txt",
    paragraphs: [
        "dinolab is a creative marketing studio of strategists, designers, copywriters and media buyers. We started with one simple belief: a brand should never blend into the background.",
        "So we mix sharp data with a healthy dose of play. Every campaign we build has a clear goal, a number to beat and an idea people actually want to look at."
    ],
    signature: "the dinolab crew"
}

export const PRINCIPLES: ReadonlyArray<Principle> = [
    {
        title: "bold ideas",
        text: "We chase the idea that makes people stop scrolling, then make it work for your numbers.",
        icon: "sparkle"
    },
    {
        title: "data, not guesses",
        text: "Every campaign is tracked, tested and tuned, so you always know what your budget is doing.",
        icon: "check"
    },
    {
        title: "results that roar",
        text: "We measure success in leads, sales and loyal fans, not in pretty reports.",
        icon: "heart"
    }
]

export const SERVICES: ReadonlyArray<Service> = [
    {
        id: "branding",
        title: "branding & identity",
        summary: "Logos, visual systems and a voice that make your brand recognizable at a glance.",
        deliverables: ["brand strategy", "logo & visual identity", "tone of voice", "brand guidelines"]
    },
    {
        id: "web",
        title: "web design & development",
        summary: "Fast, memorable websites built to turn curious visitors into paying customers.",
        deliverables: ["ux research", "ui design", "development", "cms & analytics setup"]
    },
    {
        id: "social",
        title: "social media marketing",
        summary: "Content and community management that gets people talking about you.",
        deliverables: ["content plans", "reels & short video", "community management", "influencer campaigns"]
    },
    {
        id: "ads",
        title: "performance advertising",
        summary: "Paid campaigns on Google, Meta and TikTok, tuned for every cent of your budget.",
        deliverables: ["media planning", "ad creatives", "a/b testing", "conversion tracking"]
    },
    {
        id: "seo",
        title: "seo & content",
        summary: "Content that ranks, answers real questions and keeps bringing in traffic for years.",
        deliverables: ["technical audit", "keyword strategy", "articles & landing pages", "link building"]
    },
    {
        id: "video",
        title: "video & motion",
        summary: "Scroll-stopping videos, animations and product shoots for every channel.",
        deliverables: ["concept & scripting", "production", "motion design", "ugc content"]
    }
]

export const AUDIENCES: ReadonlyArray<Audience> = [
    {
        id: "corporations",
        title: "small, medium and large corporations",
        text: "Our company offers comprehensive marketing solutions for companies of all sizes.",
        icon: "robot"
    },
    {
        id: "ecommerce",
        title: "e-commerce platforms",
        text: "Our services specialize in the promotion of online stores and marketplaces.",
        icon: "eye"
    },
    {
        id: "startups",
        title: "startups and tech products",
        text: "We help new products find their first fans, then turn that buzz into steady growth.",
        icon: "cloud"
    },
    {
        id: "charity",
        title: "charitable organizations",
        text: "We create marketing strategies that support the noble causes of charitable initiatives.",
        icon: "heart"
    }
]

export const LEVELS: ReadonlyArray<Level> = [
    {
        level: "01",
        stage: "egg",
        title: "discovery",
        text: "We dig into your business, audience and competitors to find where the real opportunities are hiding.",
        icon: "egg"
    },
    {
        level: "02",
        stage: "hatch",
        title: "strategy",
        text: "A clear plan with channels, messages, budget and goals, so everyone knows what winning looks like.",
        icon: "hatch"
    },
    {
        level: "03",
        stage: "stomp",
        title: "launch",
        text: "Creative production and launch across the channels where your customers actually spend their time.",
        icon: "footprint"
    },
    {
        level: "04",
        stage: "roar",
        title: "growth",
        text: "Weekly optimization, honest reports and fresh experiments that keep your results climbing.",
        icon: "crown"
    }
]

export const TEAM: ReadonlyArray<TeamMember> = [
    {
        name: "Maya Collins",
        role: "founder & creative director",
        image: "/images/team/maya-collins.jpg",
        move: "turns a one-line brief into a whole campaign",
        stats: [{ label: "creativity", value: 10 }, { label: "strategy", value: 8 }]
    },
    {
        name: "Daniel Chen",
        role: "head of performance",
        image: "/images/team/daniel-chen.jpg",
        move: "finds wasted ad spend in under five minutes",
        stats: [{ label: "data", value: 10 }, { label: "patience", value: 7 }]
    },
    {
        name: "Sofia Romero",
        role: "brand designer",
        image: "/images/team/sofia-romero.jpg",
        move: "pixel-perfect logos at warp speed",
        stats: [{ label: "design", value: 10 }, { label: "coffee", value: 9 }]
    },
    {
        name: "Luka Novak",
        role: "social media lead",
        image: "/images/team/luka-novak.jpg",
        move: "spots the trend before it trends",
        stats: [{ label: "trends", value: 10 }, { label: "memes", value: 9 }]
    }
]

export const BUDGETS: ReadonlyArray<string> = [
    "under $5k",
    "$5k to $15k",
    "$15k to $50k",
    "$50k+"
]

export const INTERESTS: ReadonlyArray<string> = [
    "branding",
    "web design",
    "social media",
    "paid ads",
    "seo",
    "video"
]

export const NEXT_STEPS: ReadonlyArray<string> = [
    "we reply within one business day",
    "a free 30-minute discovery call",
    "a tailored plan with clear goals and costs"
]
